"use client";

import * as React from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";

export interface Chapter {
  /** Stable, unique identifier for the chapter. */
  id: string;
  /** Bold heading shown at the top of the preview card. */
  title: string;
  /** Supporting copy shown under the title (clamped to three lines). */
  description?: React.ReactNode;
  /** Small muted label rendered above the title (e.g. a timestamp or step no.). */
  meta?: React.ReactNode;
}

export interface ChapterScrubberProps {
  /** Chapters rendered top-to-bottom, one uniform tick each. */
  chapters: Chapter[];
  /** Which side the preview card opens toward. Auto-flips near a viewport edge. Default `"right"`. */
  side?: "left" | "right";
  /** Length a tick reaches at the crest of the magnification, in pixels. Default `56`. */
  peakLength?: number;
  /** Resting length of every tick, in pixels. Keep it small. Default `14`. */
  restLength?: number;
  /** Height of each row in pixels; the gap between ticks. Smaller = denser. Default `10`. */
  rowHeight?: number;
  /** Radius of the magnification wave, in rows — how far the rise reaches from the pointer. Default `2.25`. */
  radius?: number;
  /** Marks one chapter as the persistent current position. */
  currentIndex?: number;
  /** Fires when the active (hovered/focused) chapter changes. */
  onActiveChange?: (chapter: Chapter | null, index: number) => void;
  /** Fires when a chapter is chosen via click, Enter or Space. */
  onSelect?: (chapter: Chapter, index: number) => void;
  /** Accessible name for the rail. Default `"Chapters"`. */
  label?: string;
  className?: string;
  /** Enables the section-scoped magnetic cursor on the scrubber as one control. */
  magnetic?: boolean;
}

const CARD_WIDTH = 260;
const GAP = 20;
const POINTER_SPRING = { stiffness: 700, damping: 52, mass: 0.5 };
const STRENGTH_SPRING = { stiffness: 260, damping: 30, mass: 0.6 };

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function bump(distance: number, radius: number) {
  if (distance >= radius) return 0;
  return 0.5 * (1 + Math.cos(Math.PI * (distance / radius)));
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

interface TickProps {
  index: number;
  pointer: MotionValue<number>;
  strength: MotionValue<number>;
  radius: number;
  restLength: number;
  peakLength: number;
  isCurrent: boolean;
}

const Tick = React.memo(function Tick({
  index,
  pointer,
  strength,
  radius,
  restLength,
  peakLength,
  isCurrent,
}: TickProps) {
  const width = useTransform(() => {
    const rise = strength.get() * bump(Math.abs(index - pointer.get()), radius);
    return restLength + rise * (peakLength - restLength);
  });
  const opacity = useTransform(() => {
    const rise = strength.get() * bump(Math.abs(index - pointer.get()), radius);
    const base = isCurrent ? 0.55 : 0.22;
    return base + rise * (1 - base);
  });
  const scaleY = useTransform(() => {
    const rise = strength.get() * bump(Math.abs(index - pointer.get()), radius);
    return 1 + rise * 0.4;
  });

  return (
    <motion.span
      aria-hidden="true"
      className={classNames("chapter-scrubber__tick", isCurrent && "is-current")}
      style={{ opacity, scaleY, width }}
    />
  );
});

export function ChapterScrubber({
  chapters,
  side = "right",
  peakLength = 56,
  restLength = 14,
  rowHeight = 10,
  radius = 2.25,
  currentIndex,
  onActiveChange,
  onSelect,
  label = "Chapters",
  className,
  magnetic = false,
}: ChapterScrubberProps) {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const buttonsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
  const baseId = React.useId();
  const optionId = (index: number) => `${baseId}-opt-${index}`;

  const rawPointer = useMotionValue(0);
  const rawStrength = useMotionValue(0);
  const springPointer = useSpring(rawPointer, POINTER_SPRING);
  const springStrength = useSpring(rawStrength, STRENGTH_SPRING);
  const pointer = prefersReducedMotion ? rawPointer : springPointer;
  const strength = prefersReducedMotion ? rawStrength : springStrength;

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [engaged, setEngaged] = React.useState(false);
  const [flipped, setFlipped] = React.useState(false);
  const [cardHeight, setCardHeight] = React.useState(0);
  const hoveringRef = React.useRef(false);
  const focusedRef = React.useRef<number | null>(null);
  const pointerFocusedRef = React.useRef(false);
  const activeRef = React.useRef(0);

  const commitActive = React.useCallback((index: number) => {
    if (index !== activeRef.current) {
      activeRef.current = index;
      setActiveIndex(index);
    }
  }, []);

  const last = chapters.length - 1;

  React.useEffect(() => {
    onActiveChange?.(
      engaged ? (chapters[activeIndex] ?? null) : null,
      engaged ? activeIndex : -1,
    );
  }, [engaged, activeIndex, chapters, onActiveChange]);

  React.useEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [activeIndex]);

  React.useEffect(() => {
    if (!engaged) return;
    const element = containerRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const viewportWidth = element.ownerDocument.defaultView?.innerWidth ?? 0;
    const neededRoom = CARD_WIDTH + GAP + 8;
    let useRight = side === "right";
    if (useRight && viewportWidth - rect.right < neededRoom && rect.left >= neededRoom) useRight = false;
    if (!useRight && rect.left < neededRoom && viewportWidth - rect.right >= neededRoom) useRight = true;
    setFlipped(useRight !== (side === "right"));
  }, [engaged, activeIndex, side]);

  const resolvedSide = side === "right"
    ? flipped ? "left" : "right"
    : flipped ? "right" : "left";

  const totalHeight = chapters.length * rowHeight;
  const rovingIndex = engaged ? activeIndex : (currentIndex ?? 0);

  const cardTop = useTransform(pointer, (value) => {
    const half = cardHeight / 2;
    const center = clamp(
      (value + 0.5) * rowHeight,
      half,
      Math.max(half, totalHeight - half),
    );
    return center - half;
  });
  const cardScale = useTransform(strength, [0, 1], [0.97, 1]);
  const cardX = useTransform(
    strength,
    [0, 1],
    [resolvedSide === "right" ? -6 : 6, 0],
  );

  const engageAt = (pointerRow: number, activeAt: number) => {
    rawPointer.set(pointerRow);
    rawStrength.set(1);
    commitActive(clamp(activeAt, 0, last));
    if (!engaged) setEngaged(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = listRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const row = (event.clientY - rect.top) / rowHeight - 0.5;
    hoveringRef.current = true;
    engageAt(clamp(row, -0.5, last + 0.5), Math.round(row));
  };

  const handlePointerLeave = () => {
    hoveringRef.current = false;

    if (pointerFocusedRef.current) {
      pointerFocusedRef.current = false;
      focusedRef.current = null;
      const focusedElement = containerRef.current?.ownerDocument.activeElement;
      if (focusedElement instanceof HTMLElement && containerRef.current?.contains(focusedElement)) {
        focusedElement.blur();
      }
      rawStrength.set(0);
      setEngaged(false);
      return;
    }

    if (focusedRef.current != null) {
      rawPointer.set(focusedRef.current);
    } else {
      rawStrength.set(0);
      setEngaged(false);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      focusedRef.current = null;
      if (!hoveringRef.current) {
        rawStrength.set(0);
        setEngaged(false);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    pointerFocusedRef.current = false;
    let next = focusedRef.current ?? activeRef.current;
    if (event.key === "Enter" || event.key === " ") {
      const chapter = chapters[next];
      if (!chapter) return;
      event.preventDefault();
      onSelect?.(chapter, next);
      return;
    }
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        next = Math.min(last, next + 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        next = Math.max(0, next - 1);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    buttonsRef.current[next]?.focus();
  };

  const activeChapter = chapters[activeIndex];

  return (
    <div
      className={classNames("chapter-scrubber", className)}
      data-footer-magnetic={magnetic ? "" : undefined}
      ref={containerRef}
      style={{ width: peakLength }}
    >
      <div
        aria-activedescendant={engaged ? optionId(activeIndex) : undefined}
        aria-label={label}
        aria-orientation="vertical"
        className="chapter-scrubber__list"
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPointerLeave={handlePointerLeave}
        onPointerMove={handlePointerMove}
        ref={listRef}
        role="listbox"
      >
        {chapters.map((chapter, index) => {
          const isCurrent = index === currentIndex;
          const separator = /[.!?]$/.test(chapter.title) ? " " : ". ";
          const description = typeof chapter.description === "string"
            ? `${separator}${chapter.description}`
            : "";
          return (
            <button
              aria-label={`${chapter.title}${description}`}
              aria-selected={isCurrent}
              className={classNames("chapter-scrubber__option", `opens-${resolvedSide}`)}
              id={optionId(index)}
              key={chapter.id}
              onClick={() => onSelect?.(chapter, index)}
              onFocus={() => {
                focusedRef.current = index;
                engageAt(index, index);
              }}
              onPointerDown={() => {
                pointerFocusedRef.current = true;
              }}
              ref={(element) => {
                buttonsRef.current[index] = element;
              }}
              role="option"
              style={{ height: rowHeight }}
              tabIndex={index === rovingIndex ? 0 : -1}
              type="button"
            >
              <Tick
                index={index}
                isCurrent={isCurrent}
                peakLength={peakLength}
                pointer={pointer}
                radius={radius}
                restLength={restLength}
                strength={strength}
              />
            </button>
          );
        })}
      </div>

      {activeChapter ? (
        <motion.div
          aria-hidden="true"
          className={classNames("chapter-scrubber__card", `opens-${resolvedSide}`)}
          ref={cardRef}
          style={{
            opacity: strength,
            scale: cardScale,
            top: cardTop,
            x: cardX,
            ...(resolvedSide === "right"
              ? { left: peakLength + GAP }
              : { right: peakLength + GAP }),
          }}
        >
          {activeChapter.meta ? (
            <div className="chapter-scrubber__meta">{activeChapter.meta}</div>
          ) : null}
          <div className="chapter-scrubber__title">{activeChapter.title}</div>
          {activeChapter.description ? (
            <p className="chapter-scrubber__description">{activeChapter.description}</p>
          ) : null}
        </motion.div>
      ) : null}
    </div>
  );
}

export default ChapterScrubber;
